import { api } from './api';
import { dedupedFetch, invalidateCache } from './cache';
import { fetchTodasSelecoesByUser } from './selecao-etapa3.service';
import type {
  VotoPreliminarEtapa3,
  VotoPreliminarEtapa3Input,
} from 'types/etapa3';

const PATH = '/database/voto_preliminar_etapa3__c';

const assessorKey = (assessorId: string) =>
  `preliminar-etapa3-assessor-${assessorId}`;
const vpKey = (vpMatricula: string) => `preliminar-etapa3-vp-${vpMatricula}`;

// ─── Leitura ──────────────────────────────────────────────────────────────────

const fetchPreliminarAtivoByAssessorRaw = async (
  // assessorId é derivado de context.player no servidor; mantido p/ a chave de cache.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  assessorId: string
): Promise<VotoPreliminarEtapa3 | null> => {
  const { data } = await api.post('/find/preliminar_etapa3_ativo_by_assessor', {});
  const results = Array.isArray(data) ? data : [];
  return results.length > 0 ? (results[0] as VotoPreliminarEtapa3) : null;
};

const fetchUltimoPreliminarByAssessorRaw = async (
  // assessorId é derivado de context.player no servidor; mantido p/ a chave de cache.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  assessorId: string
): Promise<VotoPreliminarEtapa3 | null> => {
  const { data } = await api.post('/find/preliminar_etapa3_ultimo_by_assessor', {});
  const results = Array.isArray(data) ? data : [];
  return results.length > 0 ? (results[0] as VotoPreliminarEtapa3) : null;
};

export const fetchUltimoPreliminarByAssessor = (
  assessorId: string
): Promise<VotoPreliminarEtapa3 | null> =>
  dedupedFetch(
    assessorKey(assessorId),
    () => fetchUltimoPreliminarByAssessorRaw(assessorId),
    15_000
  );

// Busca até 10 preliminares ativos para esse VP. O limite folgado permite que o
// caller (hook) escolha o primeiro doc que pertence ao produto atual, já que a
// collection voto_preliminar_etapa3__c é compartilhada ELBB/Conexão.
const fetchPreliminaresEmAprovacaoByVpRaw = async (
  // vpMatricula é derivado de context.player no servidor; mantido p/ a chave de cache.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  vpMatricula: string
): Promise<VotoPreliminarEtapa3[]> => {
  const { data } = await api.post('/find/preliminares_etapa3_em_aprovacao_by_vp', {});
  return Array.isArray(data) ? (data as VotoPreliminarEtapa3[]) : [];
};

/**
 * Retorna o preliminar pendente do VP. Quando `pertenceAoProduto` é passado,
 * filtra para considerar apenas docs cujo `notasRascunho` casa com o produto
 * (ELBB ou Conexão). Sem o filtro, devolve o mais recente — comportamento
 * legado.
 */
export const fetchPreliminarEmAprovacaoByVp = async (
  vpMatricula: string,
  pertenceAoProduto?: (notas?: Record<string, unknown> | null) => boolean
): Promise<VotoPreliminarEtapa3 | null> => {
  const lista = await dedupedFetch(
    vpKey(vpMatricula),
    () => fetchPreliminaresEmAprovacaoByVpRaw(vpMatricula),
    30_000
  );
  if (!pertenceAoProduto) {
    return lista.length > 0 ? lista[0] : null;
  }
  const matched = lista.find((p) => pertenceAoProduto(p.notasRascunho));
  return matched ?? null;
};

export const invalidarCachePreliminar = (
  assessorId: string,
  vpMatricula?: string
): void => {
  invalidateCache(assessorKey(assessorId));
  if (vpMatricula) invalidateCache(vpKey(vpMatricula));
};

// ─── Escrita (padrão supersede) ───────────────────────────────────────────────

/**
 * Escreve um novo preliminar e supersede o anterior — desde que o anterior
 * pertença ao mesmo produto do novo (verificado via `pertenceAoProduto`). Sem
 * o filtro, supersede qualquer ativo (comportamento legado).
 */
const escreverPreliminar = async (
  input: VotoPreliminarEtapa3Input,
  pertenceAoProduto?: (notas?: Record<string, unknown> | null) => boolean
): Promise<VotoPreliminarEtapa3> => {
  const previous = await fetchPreliminarAtivoByAssessorRaw(input.assessorId);
  if (previous && (!pertenceAoProduto || pertenceAoProduto(previous.notasRascunho))) {
    await api.put(PATH, { ...previous, supersededAt: Date.now() });
  }
  const { data } = await api.post(PATH, input);
  invalidarCachePreliminar(input.assessorId, input.vpMatricula);
  return data as VotoPreliminarEtapa3;
};

export const salvarPreliminarRascunho = (
  input: VotoPreliminarEtapa3Input,
  pertenceAoProduto?: (notas?: Record<string, unknown> | null) => boolean
): Promise<VotoPreliminarEtapa3> =>
  escreverPreliminar({ ...input, status: 'rascunho' }, pertenceAoProduto);

export const enviarPreliminarParaAprovacao = async (
  input: VotoPreliminarEtapa3Input,
  pertenceAoProduto?: (notas?: Record<string, unknown> | null) => boolean
): Promise<VotoPreliminarEtapa3> => {
  // Defensivo: se o VP já enviou o voto final neste produto, o preliminar é
  // desconsiderado. Como selecao_etapa3__c é compartilhado, filtra por produto
  // — votos do VP em outro produto não devem bloquear o envio aqui.
  const selecoesVp = await fetchTodasSelecoesByUser(input.vpMatricula);
  const selecoesDoProduto = pertenceAoProduto
    ? selecoesVp.filter((s) => pertenceAoProduto({ [s.categoria]: true }))
    : selecoesVp;
  const vpJaVotou = selecoesDoProduto.some(
    (s) => (s.iniciativaIds?.length ?? 0) > 0
  );
  if (vpJaVotou) {
    throw new Error('VP_JA_VOTOU');
  }
  return escreverPreliminar(
    {
      ...input,
      status: 'em_aprovacao',
      enviadoEm: input.enviadoEm ?? Date.now(),
    },
    pertenceAoProduto
  );
};

export const marcarPreliminarSuperseded = async (
  vpMatricula: string,
  pertenceAoProduto?: (notas?: Record<string, unknown> | null) => boolean
): Promise<void> => {
  const lista = await fetchPreliminaresEmAprovacaoByVpRaw(vpMatricula);
  const preliminar = pertenceAoProduto
    ? lista.find((p) => pertenceAoProduto(p.notasRascunho))
    : lista[0];
  if (!preliminar) return;
  await api.put(PATH, { ...preliminar, supersededAt: Date.now() });
  invalidarCachePreliminar(preliminar.assessorId, vpMatricula);
};
